import {
  NewSurvey,
  OrderReview,
  Question,
  Survey,
  SurveyTrigger,
} from "@/types/surveys";

export interface SurveyTemplate {
  id: string;
  name: string;
  badge: string;
  description: string;
  survey: Partial<NewSurvey>;
}

export const DEFAULT_POST_ORDER_QUESTIONS: Question[] = [
  {
    id: "q_overall_rating",
    title: "¿Cómo calificarías tu experiencia general con tu pedido?",
    type: "rating",
    required: true,
    isFixed: true,
    options: [],
  },
  {
    id: "q_food_quality",
    title: "¿Cómo estuvieron tus alimentos?",
    type: "single_choice",
    required: true,
    isFixed: true,
    options: [
      { id: "opt_excellent", title: "Excelentes, sabor y temperatura ideales" },
      { id: "opt_good", title: "Buenos, cumplió lo esperado" },
      { id: "opt_cold", title: "Llegaron fríos o mal empacados" },
      { id: "opt_incomplete", title: "Faltó algún complemento o ingrediente" },
    ],
  },
  {
    id: "q_comments",
    title: "¿Tienes algún comentario o sugerencia adicional?",
    type: "text",
    required: false,
    isFixed: true,
    options: [],
  },
];

export const DEFAULT_POST_ORDER_SURVEY: Partial<NewSurvey> = {
  title: "¿Cómo estuvo tu pedido?",
  description: "Tu opinión nos ayuda a mejorar cada día.",
  trigger: "post_order",
  isPublished: true,
  status: "active",
  questions: DEFAULT_POST_ORDER_QUESTIONS,
};

export const isFixedPostOrderQuestion = (
  trigger: SurveyTrigger | undefined,
  question: Question,
  index: number
): boolean => {
  if (trigger !== "post_order") return false;
  if (question.isFixed) return true;
  if (
    question.id === "q_overall_rating" ||
    question.id === "q_food_quality" ||
    question.id === "q_comments"
  ) {
    return true;
  }
  return index < 3;
};

export const extractOrderReview = (
  survey: Pick<Survey, "id" | "questions">,
  responseId: string,
  answers: Record<string, any>,
  createdAt?: number
): OrderReview => {
  const questions = survey.questions ?? [];

  const ratingQuestion =
    questions.find((q) => q.id === "q_overall_rating") ??
    questions.find((q) => q.type === "rating");
  const foodQuestion =
    questions.find((q) => q.id === "q_food_quality") ??
    questions.find((q) => q.type === "single_choice");
  const commentQuestion =
    questions.find((q) => q.id === "q_comments") ??
    questions.find((q) => q.type === "text");

  const getAnswerVal = (q?: Question) => {
    if (!q || !answers) return undefined;
    if (Array.isArray(answers)) {
      return answers.find((a: any) => a.question === q.title)?.answer;
    }
    return answers[q.id] ?? answers[q.title];
  };

  const rawRating = getAnswerVal(ratingQuestion);
  const numRating = Number(rawRating);
  const rating = !Number.isNaN(numRating) && numRating >= 1 && numRating <= 5 ? numRating : 0;

  let foodQuality: string | null = null;
  const rawFood = getAnswerVal(foodQuestion);
  if (rawFood !== undefined && rawFood !== null && rawFood !== "") {
    const matchedOpt = (foodQuestion?.options ?? []).find(
      (opt, idx) =>
        (opt.id || String(idx)) === String(rawFood) || opt.title === String(rawFood)
    );
    foodQuality = matchedOpt ? matchedOpt.title : String(rawFood);
  }

  const rawComment = getAnswerVal(commentQuestion);
  const comment =
    typeof rawComment === "string" && rawComment.trim().length > 0
      ? rawComment.trim()
      : null;

  return {
    surveyId: survey.id,
    responseId,
    rating,
    foodQuality,
    comment,
    createdAt: createdAt || Date.now(),
  };
};

export const SURVEY_TEMPLATES: SurveyTemplate[] = [
  {
    id: "dine_in_experience",
    name: "Atención en Mesa (Restaurante)",
    badge: "En Local",
    description:
      "Ideal para código QR en mesa. Evalúa la atención del mesero, limpieza y ambiente del local.",
    survey: {
      title: "Evalúa tu visita",
      description: "Cuéntanos cómo fue tu experiencia hoy en nuestro local.",
      trigger: "dine_in",
      isPublished: false,
      questions: [
        {
          id: "q_service_rating",
          title: "¿Cómo calificarías la atención recibida en tu mesa?",
          type: "rating",
          required: true,
          options: [],
        },
        {
          id: "q_speed",
          title: "¿El tiempo de espera de tus platillos fue adecuado?",
          type: "single_choice",
          required: true,
          options: [
            { id: "opt_fast", title: "Sí, fue muy rápido" },
            { id: "opt_normal", title: "Normal, dentro de lo esperado" },
            { id: "opt_slow", title: "Tardó más de lo esperado" },
          ],
        },
        {
          id: "q_feedback",
          title: "¿Qué fue lo que más te gustó o qué podríamos mejorar?",
          type: "text",
          required: false,
          options: [],
        },
      ],
    },
  },
  {
    id: "menu_discovery",
    name: "Preferencias de Menú",
    badge: "Producto",
    description:
      "Descubre qué platillos prefieren tus clientes o qué les gustaría que agregues próximamente.",
    survey: {
      title: "Ayúdanos a crear nuestro próximo platillo",
      description: "Queremos saber qué te gustaría ver en nuestro menú.",
      trigger: "manual",
      isPublished: false,
      questions: [
        {
          id: "q_improvements",
          title: "¿Qué te gustaría que agreguemos al menú?",
          type: "multiple_choice",
          required: true,
          options: [
            { id: "opt_combos", title: "Más promociones y combos familiares" },
            { id: "opt_desserts", title: "Nuevos postres y bebidas" },
            { id: "opt_healthy", title: "Opciones saludables / ligeras" },
          ],
        },
        {
          id: "q_dish_idea",
          title: "¿Algún platillo en especial que te encantaría probar?",
          type: "text",
          required: false,
          options: [],
        },
      ],
    },
  },
];
